/**
 * <App /> — provider tree + router.
 *
 * Tree: Theme → QueryClient → RouterProvider → root layout (OidcTokenBridge →
 *       SignOut → AuthProvider → ConfirmProvider → Toaster).
 *
 * Routes mirror the optum-agent admin surface.
 */
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';

import { AuthProvider } from './auth/AuthContext.js';
import { signOut as signOutOfRealm } from './auth/oidc.js';
import { OidcTokenBridge } from './auth/oidc-bridge.js';
import { SignOutProvider } from './auth/sign-out.js';
import { ThemeProvider } from './theme/ThemeContext.js';
import {
  KeepQueryRedirect,
  OrgRecordRedirect,
  OrgScopedRedirect,
} from './components/shell/LegacyRedirect.js';
import { ProtectedLayout } from './components/shell/ProtectedLayout.js';
import { AccountDetail } from './features/accounts/AccountDetail.js';
import { AccountsPage } from './features/accounts/AccountsPage.js';
import { AgentDetail } from './features/agents/AgentDetail.js';
import { AgentsPage } from './features/agents/AgentsPage.js';
import { AuthCallbackPage } from './features/auth/AuthCallbackPage.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { DashboardPage } from './features/dashboard/DashboardPage.js';
import { FederationPage } from './features/federation/FederationPage.js';
import { PartnerDetail } from './features/federation/PartnerDetail.js';
import { OrganizationsPage } from './features/organizations/OrganizationsPage.js';
import { NewOrganizationPage } from './features/registrations/NewOrganizationPage.js';
import { OrgConfigPage } from './features/org-workspace/OrgConfigPage.js';
import { OrgOverviewPage } from './features/org-workspace/OrgOverviewPage.js';
import { OrgGroupsPage } from './features/org-workspace/OrgGroupsPage.js';
import { OrgUsersPage } from './features/org-users/OrgUsersPage.js';
import { PlatformCostPage } from './features/cost/PlatformCostPage.js';
import { OrgCostPage } from './features/org-workspace/OrgCostPage.js';
import { OrgSecretsPage } from './features/org-workspace/OrgSecretsPage.js';
import { DeliveriesPage } from './features/webhooks/DeliveriesPage.js';
import { PlatformStaffPage } from './features/platform-staff/PlatformStaffPage.js';
import { PluginDetail } from './features/plugins/PluginDetail.js';
import { PluginReleasePage } from './features/plugins/PluginReleasePage.js';
import { PluginsPage } from './features/plugins/PluginsPage.js';
import { LinkageEditorPage } from './features/linkages/LinkageEditorPage.js';
import { AutomationPage } from './features/automation/AutomationPage.js';
import { OrgAutomationPage } from './features/automation/OrgAutomationPage.js';
import { SandboxPage } from './features/sandbox/SandboxPage.js';
import { SettingsPage } from './features/settings/SettingsPage.js';
import { SecurityPage } from './features/security/SecurityPage.js';
import { CollectionsPage } from './features/collections/CollectionsPage.js';
import { MigratePage } from './features/collections/MigratePage.js';
import { TaskDetailPage } from './features/tasks/TaskDetailPage.js';
import { TasksPage } from './features/tasks/TasksPage.js';
import { queryClient } from './lib/query-client.js';
import { ConfirmProvider } from './ui/Confirm.js';
import { Toaster } from './ui/Toaster.js';

/**
 * The routes, under one root layout that holds the providers needing the
 * router (token bridge, sign-out, auth, confirm). A data router, so a form
 * with unsaved changes can block an in-app navigation (Feature 70).
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      <Route element={<ProtectedLayout />}>
        {/* ── Platform workspace ── */}
        <Route path="/" element={<Navigate to="/overview" replace />} />
        <Route path="/overview" element={<DashboardPage />} />
        <Route path="/review" element={<Navigate to="/overview" replace />} />
        <Route path="/staff" element={<PlatformStaffPage />} />

        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/organizations/new" element={<NewOrganizationPage />} />
        <Route path="/organizations/:id" element={<OrgRecordRedirect />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/:externalSub" element={<AccountDetail />} />

        <Route path="/plugins" element={<PluginsPage />} />
        <Route path="/plugins/:kind/:name" element={<PluginDetail />} />
        <Route path="/plugins/:kind/:name/release" element={<PluginReleasePage />} />
        <Route path="/automation" element={<AutomationPage />} />
        <Route path="/automation/linkages/new" element={<LinkageEditorPage />} />
        <Route path="/automation/linkages/:key" element={<LinkageEditorPage />} />
        {/* Feature 77 retired the trigger templates; their forms land on Automation. */}
        <Route path="/automation/triggers/new" element={<Navigate to="/automation" replace />} />
        <Route
          path="/automation/triggers/:id/edit"
          element={<Navigate to="/automation" replace />}
        />

        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/cost" element={<PlatformCostPage />} />
        <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
        <Route path="/webhook-deliveries" element={<DeliveriesPage />} />
        <Route path="/sandbox" element={<SandboxPage />} />

        <Route path="/federation" element={<FederationPage />} />
        <Route path="/federation/partners/:id" element={<PartnerDetail />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* ── Organization workspace ──
          Every tenant-scoped surface lives under /orgs/:orgId, so
          the tenant is in the URL and survives a refresh. */}
        <Route path="/orgs/:orgId" element={<Navigate to="overview" replace />} />
        <Route path="/orgs/:orgId/overview" element={<OrgOverviewPage />} />
        <Route path="/orgs/:orgId/agents" element={<AgentsPage />} />
        <Route path="/orgs/:orgId/agents/:id" element={<AgentDetail />} />
        <Route path="/orgs/:orgId/collections" element={<CollectionsPage />} />
        <Route path="/orgs/:orgId/collections/migrate" element={<MigratePage />} />
        <Route path="/orgs/:orgId/users" element={<OrgUsersPage />} />
        <Route path="/orgs/:orgId/groups" element={<OrgGroupsPage />} />
        <Route path="/orgs/:orgId/config" element={<OrgConfigPage />} />
        <Route path="/orgs/:orgId/secrets" element={<OrgSecretsPage />} />
        <Route
          path="/orgs/:orgId/triggers"
          element={<KeepQueryRedirect to={(p) => `/orgs/${p['orgId'] ?? ''}/automation`} />}
        />
        <Route path="/orgs/:orgId/automation" element={<OrgAutomationPage />} />
        <Route path="/orgs/:orgId/onboarding" element={<Navigate to="../overview" replace />} />
        <Route path="/orgs/:orgId/tasks" element={<TasksPage />} />
        <Route path="/orgs/:orgId/cost" element={<OrgCostPage />} />
        <Route path="/orgs/:orgId/tasks/:taskId" element={<TaskDetailPage />} />

        {/* ── Legacy URLs ──
          Operators have these bookmarked. Keep for at least one
          release after the IA change ships. */}
        <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
        <Route path="/registrations" element={<Navigate to="/overview" replace />} />
        <Route path="/platform-staff" element={<Navigate to="/staff" replace />} />
        <Route path="/capabilities" element={<Navigate to="/plugins?tab=capabilities" replace />} />
        <Route path="/triggers" element={<Navigate to="/automation" replace />} />
        <Route path="/payload-mapping-templates" element={<Navigate to="/automation" replace />} />
        {/* Feature 74 moved Linkages and the trigger templates under Automation. */}
        <Route path="/linkages" element={<KeepQueryRedirect to="/automation" />} />
        <Route path="/linkages/new" element={<KeepQueryRedirect to="/automation/linkages/new" />} />
        <Route
          path="/linkages/:key"
          element={<KeepQueryRedirect to={(p) => `/automation/linkages/${p['key'] ?? ''}`} />}
        />
        <Route path="/plugins/templates/new" element={<Navigate to="/automation" replace />} />
        <Route path="/plugins/templates/:id/edit" element={<Navigate to="/automation" replace />} />
        <Route path="/federated-partners" element={<Navigate to="/federation" replace />} />
        <Route path="/secret-fields" element={<Navigate to="/security" replace />} />
        <Route
          path="/service-tokens"
          element={<Navigate to="/security?tab=service-tokens" replace />}
        />
        <Route
          path="/agents"
          element={<OrgScopedRedirect param="organization_id" section="agents" />}
        />
        <Route path="/agents/:id" element={<AgentDetail />} />
        <Route
          path="/collections"
          element={<OrgScopedRedirect param="org" section="collections" />}
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
  { future: { v7_relativeSplatPath: true } },
);

function RootLayout() {
  return (
    <OidcTokenBridge>
      <OidcSignOut>
        <AuthProvider>
          <ConfirmProvider>
            <Outlet />
            <Toaster />
          </ConfirmProvider>
        </AuthProvider>
      </OidcSignOut>
    </OidcTokenBridge>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * Publishes sign-out to the shell. The query cache is cleared first so the
 * cached principal cannot outlive the realm session, then the realm ends the
 * session and sends the browser back to /login.
 */
function OidcSignOut({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  async function signOut() {
    queryClient.clear();
    await signOutOfRealm();
  }

  return <SignOutProvider signOut={signOut}>{children}</SignOutProvider>;
}

function NotFound() {
  return (
    <div style={{ padding: 22 }}>
      <h1 style={{ fontSize: 'var(--text-page)', fontWeight: 'var(--weight-semibold)' }}>404</h1>
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        No route matched.
      </p>
    </div>
  );
}
